import { DeliveryState, ScheduledDeliveryState, simulateLatency, type ApiContext } from '../shared';
import type { Delivery } from '../Deliveries';
import { STUB_SCHEDULED_DELIVERIES } from './stubData';
import type {
  ScheduledDelivery,
  CreateScheduledDeliveryRequest,
  UpdateScheduledDeliveryRequest,
  ListScheduledDeliveriesParams,
  ListScheduledDeliveriesResponse,
  RunScheduledDeliveryResponse,
} from './types';
import { apiClient } from '@admin-ui-root/src/app/lib/apiClient';
import { fileDeliveryApiUrl } from '@admin-ui-root/src/app/pages/FileDelivery/api/api';

export const listScheduledDeliveries = async (
  ctx: ApiContext,
  params?: ListScheduledDeliveriesParams,
): Promise<ListScheduledDeliveriesResponse> => {
  const api = apiClient();

  const searchParams = new URLSearchParams();
  if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
  if (params?.pageToken) searchParams.append('pageToken', params.pageToken);
  if (params?.filter) searchParams.append('filter', params.filter);
  if (params?.orderBy) searchParams.append('orderBy', params.orderBy);

  const queryString = searchParams.toString();
  const url = queryString ? `scheduledDeliveries?${queryString}` : 'scheduledDeliveries';

  const response = await api
    .get(fileDeliveryApiUrl(url), {
      headers: {
        'x-fds-profile': ctx.serviceAccount,
        'x-c2fo-dc': ctx.datacenter,
      },
    })
    .json<unknown>();

  if (!response || typeof response !== 'object' || !('scheduledDeliveries' in response)) {
    return {
      scheduledDeliveries: [],
      totalSize: 0,
    };
  }

  const typedResponse = response as ListScheduledDeliveriesResponse;
  return {
    scheduledDeliveries: typedResponse.scheduledDeliveries || [],
    nextPageToken: typedResponse.nextPageToken,
    totalSize: typedResponse.totalSize || 0,
  };
};

export const getScheduledDelivery = async (ctx: ApiContext, name: string): Promise<ScheduledDelivery | null> => {
  const api = apiClient();

  try {
    const response = await api
      .get(fileDeliveryApiUrl(name), {
        headers: {
          'x-fds-profile': ctx.serviceAccount,
          'x-c2fo-dc': ctx.datacenter,
        },
      })
      .json<{ scheduledDelivery: ScheduledDelivery }>();

    return response.scheduledDelivery;
  } catch (error) {
    return null;
  }
};

export const createScheduledDelivery = async (
  ctx: ApiContext,
  _request: CreateScheduledDeliveryRequest,
): Promise<ScheduledDelivery> => {
  // TODO: Replace with real API call when available
  // No-op implementation - returns a mock scheduled delivery
  await simulateLatency();

  const now = new Date().toISOString();
  const newScheduledDelivery: ScheduledDelivery = {
    ..._request.scheduledDelivery,
    name: `scheduledDeliveries/${crypto.randomUUID()}`,
    uid: crypto.randomUUID(),
    state: ScheduledDeliveryState.SCHEDULED_DELIVERY_STATE_ACTIVE,
    createTime: now,
    updateTime: now,
  };

  return newScheduledDelivery;
};

export const updateScheduledDelivery = async (
  ctx: ApiContext,
  _request: UpdateScheduledDeliveryRequest,
): Promise<ScheduledDelivery> => {
  // TODO: Replace with real API call when available
  // No-op implementation
  await simulateLatency();

  const existing = STUB_SCHEDULED_DELIVERIES.find((sd) => sd.name === _request.scheduledDelivery.name);
  if (!existing) {
    throw new Error(`Scheduled delivery not found: ${_request.scheduledDelivery.name}`);
  }

  return {
    ...existing,
    ..._request.scheduledDelivery,
    updateTime: new Date().toISOString(),
  };
};

export const deleteScheduledDelivery = async (_ctx: ApiContext, _name: string): Promise<void> => {
  // TODO: Replace with real API call when available
  // No-op implementation
  await simulateLatency();
};

export const pauseScheduledDelivery = async (_ctx: ApiContext, name: string): Promise<ScheduledDelivery> => {
  // TODO: Replace with real API call when available
  // No-op implementation
  await simulateLatency();

  const existing = STUB_SCHEDULED_DELIVERIES.find((sd) => sd.name === name || sd.uid === name);
  if (!existing) {
    throw new Error(`Scheduled delivery not found: ${name}`);
  }

  return {
    ...existing,
    state: ScheduledDeliveryState.SCHEDULED_DELIVERY_STATE_PAUSED,
    nextExecutionTime: undefined,
    updateTime: new Date().toISOString(),
  };
};

export const resumeScheduledDelivery = async (_ctx: ApiContext, name: string): Promise<ScheduledDelivery> => {
  // TODO: Replace with real API call when available
  // No-op implementation
  await simulateLatency();

  const existing = STUB_SCHEDULED_DELIVERIES.find((sd) => sd.name === name || sd.uid === name);
  if (!existing) {
    throw new Error(`Scheduled delivery not found: ${name}`);
  }

  return {
    ...existing,
    state: ScheduledDeliveryState.SCHEDULED_DELIVERY_STATE_ACTIVE,
    nextExecutionTime: new Date(Date.now() + 3600000).toISOString(),
    updateTime: new Date().toISOString(),
  };
};

export const cancelScheduledDelivery = async (_ctx: ApiContext, name: string): Promise<ScheduledDelivery> => {
  // TODO: Replace with real API call when available
  // No-op implementation
  await simulateLatency();

  const existing = STUB_SCHEDULED_DELIVERIES.find((sd) => sd.name === name || sd.uid === name);
  if (!existing) {
    throw new Error(`Scheduled delivery not found: ${name}`);
  }

  return {
    ...existing,
    state: ScheduledDeliveryState.SCHEDULED_DELIVERY_STATE_CANCELLED,
    nextExecutionTime: undefined,
    updateTime: new Date().toISOString(),
  };
};

export const runScheduledDelivery = async (_ctx: ApiContext, name: string): Promise<RunScheduledDeliveryResponse> => {
  // TODO: Replace with real API call when available
  // No-op implementation
  await simulateLatency();

  const existing = STUB_SCHEDULED_DELIVERIES.find((sd) => sd.name === name || sd.uid === name);
  if (!existing) {
    throw new Error(`Scheduled delivery not found: ${name}`);
  }

  const delivery: Delivery = {
    name: `deliveries/${crypto.randomUUID()}`,
    scheduledDelivery: existing.name,
    sourceLocation: existing.sourceLocation,
    sourcePath: existing.sourcePath,
    targetLocation: existing.targetLocation,
    targetPath: existing.targetPath,
    state: DeliveryState.DELIVERY_STATE_PENDING,
    statusMessage: 'Manual run triggered',
    fileTransfers: [],
    startTime: new Date().toISOString(),
  };

  return { delivery };
};
